import { useMemo, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import SelectableGrid from "../../components/common/SelectableGrid.jsx";
import Activities from "../../components/settings/Activities.jsx";
import GeneralSettings from "../../components/settings/GeneralSettings.jsx";
import Personas from "../../components/settings/Personas.jsx";
import SettingsGroup, { SettingsGroupNav, ONDERSTEUNING_GROUPS } from '../../components/settings/SettingsGroup';
import BillingRulesSettings from '../../components/settings/BillingRulesSettings.jsx';
import Adresses from "../../components/settings/Adresses.jsx";
import Team from "../../components/settings/Team.jsx";
import UserRoles from "../../components/settings/UserRoles.jsx";
import Data from "../../components/settings/Data.jsx";
import PortfolioContext from "../../components/settings/PortfolioContext.jsx";
import ProjectLabelsConfig from "../../components/settings/ProjectLabelsConfig.jsx";
import Subscription from "../../components/settings/Subscription.jsx";
import Partners from "../../components/settings/Partners.jsx";
import InterventionDoseSettings from "../../components/settings/InterventionDoseSettings.jsx";
import MfaRecovery from "../../components/settings/MfaRecovery.jsx";
import useSettings from "../../hooks/Settings";
import Location from "../../hooks/Location";
import useScrollToHash from "../../hooks/useScrollToHash";
import { client } from "../../hooks/Client";
import usePermissions from "../../hooks/usePermissions";

// Super-admin tenant — alleen daar tonen we het Partners-beheer-tabblad.
// Zelfde constante als in src/pages/portfolio/ProjectsOverview.jsx.
const SUPER_ADMIN = '123423HJKHVKH'

const Settings = () => {
  const settings = useSettings();
  const impactFunctionality = settings?.impactFunctionality;
  const portfolioEnabled = settings?.portfolio === "block";
  const personasOn = settings?.personasOn === "flex";
  const housevisits = settings?.housevisits;
  const settingFromUrl = Location()[3];
  // Een link als /settings/6#settings-trajecten (vanaf de pagina Facturatie) brengt het blok in beeld.
  const { hash } = useLocation();
  useScrollToHash(hash);
  const history = useHistory();
  const { has, ready } = usePermissions();

  const [selectedOption, setSelectedOption] = useState(settingFromUrl || "1");

  // Options
  const selectOptions = useMemo(() => {
    const options = [
      { id: "1", label: "Algemeen", permission: "settings.view" },
      // Abonnement-tabblad niet tonen in de super-admin tenant
      ...(client === SUPER_ADMIN ? [] : [{ id: "7", label: "Abonnement" }]),
      { id: "2", label: "Team", permission: "team.view" },
      { id: "9", label: "Rollen" },
      { id: "3", label: "Data", permission: "data.view" },
    ];

    if (impactFunctionality) {
      options.push({ id: "4", label: "Activiteiten", permission: "strategy.view" });
      // options.push({ id: "5", label: "Impact per euro" });
    }

    if (portfolioEnabled) {
      options.push({ id: "5", label: "Portfolio" });
    }

    if (personasOn || housevisits) {
      options.push({ id: "6", label: "Ondersteuning", permission: ["personas.view", "housevisits.view"] });
    }

    // Partners-beheer en accountherstel alleen in super-admin tenant
    if (client === SUPER_ADMIN) {
      options.push({ id: "8", label: "Partners" });
      options.push({ id: "10", label: "Accounts" });
    }

    // Filter once permissions have loaded; while loading, show everything
    // so the tab strip doesn't flash empty.
    if (!ready) return options;
    return options.filter((o) => {
      if (!o.permission) return true;
      return Array.isArray(o.permission) ? o.permission.some(has) : has(o.permission);
    });
  }, [impactFunctionality, personasOn, portfolioEnabled, housevisits, has, ready]);

  // Het gekozen tabblad moet er ook echt zijn. De strook wordt per rol
  // gefilterd, dus een rol zonder 'settings.view' kreeg met de vaste
  // beginwaarde "1" een tabblad dat voor haar niet bestaat: strook zichtbaar,
  // eronder niets. Zolang de rechten laden staat alles nog in de lijst, dus
  // dit valt niet alvast verkeerd terug.
  // AI-CONTEXT: See docs/pitfalls.md#state-gate-defaults-open-while-loading
  // Facturatie gaat over deelnemers, en tarieven zijn niet voor iedereen
  // (docs/features/facturatie-per-deelnemer.md). `has` is false zolang de
  // rechten laden, dus de groep valt dan dicht in plaats van open.
  const canSeeBilling = personasOn && has('billing.view');
  const ondersteuningGroups = ONDERSTEUNING_GROUPS.filter((g) => g.id !== 'settings-facturatie' || canSeeBilling);

  const activeOption = selectOptions.some((o) => o.id === selectedOption)
    ? selectedOption
    : selectOptions[0]?.id ?? selectedOption;

  const handleSelectOption = (value) => {
    const nextId = typeof value === "string" ? value : value?.id;
    if (!nextId) return;
    setSelectedOption(nextId);
    // De pagina leest het tabblad uit de URL (/settings/<id>) bij het laden,
    // dus een klik hoort hem daar ook neer te zetten — anders valt een
    // herlaadactie terug op 'Algemeen' (testronde 2026-10-01, B4). replace,
    // geen push: de terugknop hoort niet langs elk tabblad te gaan.
    history.replace(`/${client}/settings/${nextId}`);
  };

  return (
    <div className="main">
      <div className="main-container">
        <div className="page-header">
          <h1>Instellingen</h1>
        </div>

        <SelectableGrid
          items={selectOptions}
          selectedId={activeOption}
          getId={(item) => item.id}
          getLabel={(item) => item.label}
          onClick={handleSelectOption}
        />

        <GeneralSettings selectedOption={activeOption} />

        {/* Team settings */}
        <Team selectedOption={activeOption} setSelectedOption={setSelectedOption} />

        {/* Roles settings */}
        <UserRoles selectedOption={activeOption} />

        {/* Data settings */}
        <Data selectedOption={activeOption} />
        <Activities selectedOption={activeOption} />
        {activeOption === "4" && (
          <div className="settings-container">
            <InterventionDoseSettings />
          </div>
        )}
        {activeOption === "5" && <PortfolioContext />}
        {activeOption === "5" && <ProjectLabelsConfig />}
        {/* Ondersteuning: ankers naar de vier groepen, want het tabblad is lang. */}
        {activeOption === '6' && <SettingsGroupNav groups={ondersteuningGroups} />}
        <Personas selectedOption={activeOption} />
        {activeOption === '6' && canSeeBilling && (
          <div className="settings-container">
            <SettingsGroup id="settings-facturatie" title="Facturatie">
              <BillingRulesSettings />
            </SettingsGroup>
          </div>
        )}
        <Adresses selectedOption={activeOption} />

        {/* Subscription settings */}
        <Subscription selectedOption={activeOption} />

        {/* Partners — alleen zichtbaar in super-admin tenant */}
        {client === SUPER_ADMIN && (
          <Partners selectedOption={activeOption} />
        )}

        {/* Tweestapsverificatie herstellen — alleen in de super-admin tenant;
            de echte poort (Users.SuperAdmin) staat op de server. */}
        {client === SUPER_ADMIN && activeOption === "10" && <MfaRecovery />}
      </div>
    </div>
  );
};

export default Settings;
